<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';

const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');

const now = ref(new Date());
const forceBlocked = ref(false);
let timer = null;

onMounted(() => {
  timer = setInterval(() => {
    now.value = new Date();
  }, 30000);

  // Listen for 403 access_hours_blocked from API responses
  emitter.on('access_hours_blocked', () => {
    forceBlocked.value = true;
  });
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
  emitter.off('access_hours_blocked');
});

const currentAccount = computed(() => {
  const { accounts = [] } = currentUser.value || {};
  return accounts.find(a => a.id === currentAccountId.value) || {};
});

const isBlocked = computed(() => {
  if (forceBlocked.value) return true;

  const account = currentAccount.value;
  if (!account || !account.role) return false;
  if (account.role === 'administrator') return false;
  if (!account.access_hours_enabled) return false;

  const hours = account.access_hours;
  if (!hours || Object.keys(hours).length === 0) return false;

  const dayKey = String(now.value.getDay());
  const dayConfig = hours[dayKey];
  if (!dayConfig || !dayConfig.enabled) return true;

  const currentMinutes = now.value.getHours() * 60 + now.value.getMinutes();
  const [fromH, fromM] = (dayConfig.from || '00:00').split(':').map(Number);
  const [toH, toM] = (dayConfig.to || '23:59').split(':').map(Number);

  return currentMinutes < fromH * 60 + fromM || currentMinutes >= toH * 60 + toM;
});

const nextAllowedTime = computed(() => {
  const account = currentAccount.value;
  const hours = account?.access_hours;
  if (!hours) return '';

  const dayKey = String(now.value.getDay());
  const dayConfig = hours[dayKey];

  if (dayConfig?.enabled) {
    const currentMinutes = now.value.getHours() * 60 + now.value.getMinutes();
    const [fromH, fromM] = (dayConfig.from || '00:00').split(':').map(Number);
    if (currentMinutes < fromH * 60 + fromM) {
      return `hoje às ${dayConfig.from}`;
    }
  }

  const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  for (let i = 1; i <= 7; i++) {
    const nextDay = String((now.value.getDay() + i) % 7);
    const nextConfig = hours[nextDay];
    if (nextConfig?.enabled) {
      return `${DAYS[(now.value.getDay() + i) % 7]} às ${nextConfig.from}`;
    }
  }
  return '';
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isBlocked"
      class="fixed inset-0 z-[99999] flex items-center justify-center"
      style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)"
    >
      <div class="text-center px-8 max-w-lg">
        <div class="mx-auto mb-6 size-20 rounded-2xl bg-white/10 flex items-center justify-center">
          <span class="i-lucide-lock text-white/80 size-10" />
        </div>
        <h1 class="text-2xl font-bold text-white mb-3">
          Acesso fora do horário permitido
        </h1>
        <p class="text-base text-white/60 mb-6 leading-relaxed">
          Seu acesso à plataforma está restrito ao horário configurado pelo
          administrador. Por favor, retorne no horário permitido.
        </p>
        <div
          v-if="nextAllowedTime"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm"
        >
          <span class="i-lucide-clock size-4" />
          Próximo acesso: {{ nextAllowedTime }}
        </div>
      </div>
    </div>
  </Teleport>
</template>
